import {
  ActivityFilterKey,
  AssetChatFeedItem,
  type AssetChatStreamFilter,
  FeatureName,
  Issue as DomainIssue,
  replaceMentionPlaceholderWithNames,
  type AssetChatSideChat,
  PermissionValues,
  useAssetChatSubject,
  useCurrentAccount,
  useGetIssueMessage,
  useIsFeatureEnabledForWorkspace,
  useIssue,
  useStartSideChat,
  useWorkspace,
} from '@gripp/shared-logic';
import {
  AssetChatBand,
  AssetChatToolbar,
  AssetChatFilters,
  AssetChatFeed,
  AssetChatHeaderMenu,
  AssetChatRow,
  type AssetChatRowActions,
  type ColumnData,
  ErrorState,
  LoadingIndicator,
  ParticipantsDisclosure,
  SideChatPanel,
  SlideAction,
  SlideLayout,
  type SlideLayoutLocationState,
  ViewMode,
} from '@gripp/shared-ui';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import Toast from 'react-native-toast-message';
import { useCall } from '../../calls/CallProvider';
import { ShareIssuePanel } from '../../issues/components/shareIssuePanel';
import { getMediaBaseUrl } from '../../shared/mediaUtils';
import { Issue } from '../../issues/components/issue';
import { useActivityRoutineColumn } from '../../issues/components/useActivityRoutineColumn';
import { MediaPicker } from '../../shared/components/media/mediaPicker';
import { AssetRouteProvider } from '../assetRouteContext';
import { AssetCard } from './assetCard';
import { useCallDealer } from '../hooks/useCallDealer';
import { isActivityOrEditPage } from '../assetUtils';
import { AssetChatHeaderButton } from './assetChatHeaderButton';
import { AssetFilters } from './assetFilters';
import { ShareAssetPanel, useCanShareAsset } from './shareAssetPanel';

const ROUTINE_COLUMN_DEBUG = {
  id: 'asset-chat:routine-submit',
  ownerComponent: 'AssetChatPanel',
  ownerSource:
    'gripp-workspace/apps/gripp-web/src/assets/components/assetChatPanel.tsx',
  routePanelKey: 'asset-chat-thread-routine-submit',
  routePattern: ':assetId/chat/thread/:issueId/routine/:routineId',
  routeSource: 'gripp-workspace/apps/gripp-web/src/assets/assetSubRoutes.tsx',
  routeSymbol: 'assetSubRoutes',
};

/**
 * The asset's chat: a `SlideLayout` page in its own right (like every other
 * `assetSubRoutes` entry — `edit.tsx`, `assetDetail.tsx` — not a column
 * registered into someone else's), so it renders standalone under all three
 * mount points (`assets/:assetId/chat`, `activities/:issueId/asset/:assetId/
 * chat`, `workspaces/:workspaceId/asset/:assetId/chat`). Profile is
 * `leftColumn`, chat is `rightColumn`, an opened thread is `thirdColumn` —
 * the same column chain `activityConversationPanel.tsx` uses, adapted here
 * because this route isn't nested under an `<Outlet>` that would give it
 * `useSlidePanel`.
 *
 * Registered as the element for five assetSubRoutes paths: `chat`,
 * `chat/thread/:issueId`, `chat/thread/:issueId/routine/:routineId` (a
 * routine prompted from the thread, as `fourthColumn`),
 * `chat/thread/:issueId/settings` and `chat/sidechat/:messageId`. The last two are placeholders: the settings
 * frame arrives with Participants and Side Chat has no entry point until the
 * row overflow menu ships — both routes are registered now (structurally
 * gated the same as `chat`) and simply redirect back rather than 404ing on a
 * stray hit.
 */
export const AssetChatPanel = () => {
  const {
    assetId,
    messageId: sidechatMessageId,
    routineId,
    workspaceId: routeWorkspaceId,
  } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Neither the thread target nor the surrounding context can be read off
  // useParams().issueId: under the activities prefix the assetSubRoutes
  // spread binds `:issueId` twice — once for the activity the reader came
  // from, once for `chat/thread/:issueId` — and matching resolves to the
  // last one bound. On the bare `chat` route only the outer binding exists,
  // so the param still resolves and reading it mounted the third column with
  // the activity the reader arrived from: opening an asset's chat
  // auto-opened a thread nobody asked for. Both values therefore come from
  // the URL. Matching `/chat/thread/:id` answers "is this a thread route"
  // and "which thread" in one step, so the two can never disagree, and
  // splitting on `/asset/:assetId` recovers the prefix — the same fix
  // `assetActivity.tsx` applies to the same trap.
  const threadIssueId = location.pathname.match(/\/chat\/thread\/([^/]+)/)?.[1];

  const assetSegment = `/asset/${assetId}`;
  const isActivitiesContext = location.pathname.startsWith('/activities/');
  const isWorkspaceContext = location.pathname.startsWith('/workspaces/');
  const assetBasePath =
    isActivitiesContext || isWorkspaceContext
      ? `${location.pathname.split(assetSegment)[0]}${assetSegment}`
      : `/assets/${assetId}`;
  const listPath = isWorkspaceContext
    ? '/workspaces'
    : isActivitiesContext
      ? '/activities'
      : '/assets';
  // Under the workspaces prefix the profile is `activity/all`, not the bare
  // base path — see the flag-off Navigate below for why the bare path is a
  // dead end there. Shared with the chat back button so both land on the
  // same page.
  const assetProfilePath = isWorkspaceContext
    ? `${assetBasePath}/activity/all`
    : assetBasePath;

  const {
    subject,
    workspaceId,
    isLoading: isSubjectLoading,
    isError: isSubjectError,
    isUnavailable,
  } = useAssetChatSubject({ assetId, routeWorkspaceId });
  const { isEnabled: isChatEnabled, isLoading: isFeatureLoading } =
    useIsFeatureEnabledForWorkspace(FeatureName.AssetConversation, workspaceId);

  const call = useCall();
  const { canCallDealer, onCallDealer } = useCallDealer(
    subject?.assetId,
    subject?.workspaceAsset,
    isActivityOrEditPage(location)
  );
  const { startSideChat } = useStartSideChat();
  const { account: currentAccount } = useCurrentAccount();

  // Starting a side chat writes an IssueMessage, so it needs
  // issuemessage.create — the same gate the classic activity surface applies
  // (issue.tsx). The chat route only requires issue.read, so without this a
  // member who cannot post would get an action that always fails.
  const canStartSideChat = !!currentAccount?.hasPermission(
    PermissionValues.issuemessage.create
  );
  const [shareIssueId, setShareIssueId] = useState<string>();

  const [openBand, setOpenBand] = useState<'share' | 'filters'>();
  const [streamFilter, setStreamFilter] = useState<AssetChatStreamFilter>('all');
  const isBandOpen = openBand === 'share';

  // Gates the kebab itself, not just the Share panel inside the band: the
  // header menu's only item is Share, so a viewer who can't share must not
  // see a kebab that opens onto an empty menu. Same predicate ShareAssetPanel
  // reads (see useCanShareAsset's doc) — one gate, checked in both places.
  const { canShare: canShareAsset } = useCanShareAsset(workspaceId);

  // The Participants row's organization line is the asset's own workspace
  // name (a product decision — see gripp-3vt.6's notes), not a per-account
  // field: Account is a stitch-merged type and has no organization field a
  // Pothos root resolver can reach. `workspaceId` is this chat's already-
  // resolved subject workspace, not necessarily the viewer's active one.
  const chatWorkspace = useWorkspace(workspaceId ?? '');

  // The side-chat route carries only a messageId (it is a sibling of `chat`,
  // not nested under a thread), so the activity it belongs to is resolved from
  // the message rather than from the URL.
  const { message: sidechatOriginMessage } =
    useGetIssueMessage(sidechatMessageId);
  const { issue: sidechatIssueData } = useIssue(
    sidechatOriginMessage?.issue?.id
  );
  // `useIssue` hands back `useGetQuery`'s untyped data — same cast the
  // activity conversation panel applies to the same hook.
  const sidechatIssue = sidechatIssueData as DomainIssue | undefined;

  // A routine prompted from the thread (an alert's reminder) opens as a column
  // beside it. The thread already holds `third`, so the routine takes
  // `fourth` — the last slot — and its label picker stays inline in the form.
  const routineAssetRoute = useCallback(
    () => ({
      basePath: assetBasePath,
      listPath,
      isWorkspaceMode: isWorkspaceContext,
      issueId: undefined,
    }),
    [assetBasePath, listPath, isWorkspaceContext]
  );
  const { onOpenRoutine, routineColumn } = useActivityRoutineColumn({
    issueId: threadIssueId,
    routineId,
    conversationPath: `${assetBasePath}/chat/thread/${threadIssueId}`,
    assetRoute: routineAssetRoute,
    labelPickerPanel: false,
    debug: ROUTINE_COLUMN_DEBUG,
  });

  // Both hooks above must run on every render regardless of `assetId` — an
  // early return above them would call hooks conditionally.
  if (!assetId) {
    return null;
  }

  // Never navigate on unresolved data — the same rule AssetSubRouteGuestGate
  // (which already refused a Guest before this component ever mounts)
  // follows: hold on a spinner until the subject AND the flag both have an
  // answer, so a direct URL hit can't bounce someone before features load.
  if (isSubjectLoading || isFeatureLoading) {
    return <LoadingIndicator flexContainer />;
  }

  // A genuine query failure is not the same as "no row in this workspace" —
  // `isUnavailable` is also true on error (subject resolves undefined either
  // way), so this has to be checked first or a real failure silently
  // redirects to the profile instead of telling the viewer anything broke.
  if (isSubjectError) {
    return (
      <ErrorState
        title={t('chat.feed.errorTitle')}
        testID="asset-chat-panel-error"
      />
    );
  }

  // A resolved negative — the flag is off for this workspace, or the asset
  // has no row there — returns to the plain profile rather than /forbidden,
  // which is reserved for the Guest refusal AssetSubRouteGuestGate owns.
  // `workspaces/routes.tsx` mounts only the asset SUB-routes, so the bare
  // `/workspaces/:workspaceId/asset/:assetId` matches nothing and falls
  // through to the admin Workspaces list — `assetProfilePath` (above) routes
  // to `activity/all` instead under that prefix, which IS in `assetSubRoutes`
  // and resolves under all three prefixes.
  if (!isChatEnabled || isUnavailable || !subject) {
    return <Navigate to={assetProfilePath} replace />;
  }

  // `chat/thread/:issueId/settings` is likewise a placeholder — registered
  // now (the compact thread header's title already targets this URL) so the
  // Participants settings frame has a route to fill in. Until then, land back
  // on the thread rather than 404ing.
  if (threadIssueId && location.pathname.endsWith('/settings')) {
    return <Navigate to={`${assetBasePath}/chat/thread/${threadIssueId}`} replace />;
  }

  const locationState = location.state as SlideLayoutLocationState | null;
  const initialTransition =
    locationState?.slideLayoutInitialTransition === SlideAction.NEXT ||
    locationState?.slideLayoutInitialTransition === SlideAction.PREV
      ? locationState.slideLayoutInitialTransition
      : undefined;

  const assetRouteValue = {
    basePath: assetBasePath,
    listPath,
    isWorkspaceMode: isWorkspaceContext,
    issueId: undefined,
  };

  const onBackToList = () =>
    navigate(listPath, {
      state: { slideLayoutInitialTransition: SlideAction.PREV },
    });

  // The chat column's own back button unwinds one frame — to the asset
  // profile it opened from — not all the way out to the list. Sharing
  // onBackToList here was the bug: the profile and chat columns are
  // different frames on the stack and need different destinations.
  const onBackToProfile = () =>
    navigate(assetProfilePath, {
      state: { slideLayoutInitialTransition: SlideAction.PREV },
    });

  const leftColumn: ColumnData = {
    title: isWorkspaceContext ? 'admin.menu.workspaces' : 'assets.menu.title',
    mainComponent: (
      <AssetRouteProvider value={assetRouteValue}>
        <AssetCard assetId={assetId} hideCallDealer />
      </AssetRouteProvider>
    ),
    // Same header actions row the profile roots render (assetDetail.tsx,
    // assetDetailPanel.tsx, activityConversationPanel.tsx): the profile
    // column is the same column here, so it keeps the same controls
    // rather than dropping the icon and its badge on the way into chat.
    // The button self-gates, including for guests.
    headerComponent: (
      <View style={styles.headerActionsRow}>
        <AssetChatHeaderButton
          assetId={assetId}
          routeWorkspaceId={routeWorkspaceId}
        />
        <AssetFilters assetId={assetId} />
      </View>
    ),
    showBackButton: true,
    backButtonTransition: SlideAction.PREV,
    backButtonTransitionBehavior: 'concurrent',
    backButtonTransitionExitColumn: 'both',
    onBackButtonPress: onBackToList,
    disableScroll: false,
  };

  /**
   * The feed row's overflow menu and its side-chat pills. Every one of these
   * needs something shared-ui does not have — the router, the clipboard, the
   * call provider, the share panel — so the feed only routes them. An absent
   * handler hides its menu entry, which is how the menu varies per surface:
   * `onShare` is supplied here and nowhere on mobile, because
   * `shareIssuePanel` is web-only.
   */
  const rowActions: AssetChatRowActions = {
    onOpenSideChat: (_item, parentMessageId) =>
      navigate(`${assetBasePath}/chat/sidechat/${parentMessageId}`),
    // Creates the "Side chat started from <author>'s message" divider before
    // navigating — the same order issue.tsx uses. Navigating without it would
    // open a side chat with no system line and nothing parented to the origin,
    // which also means no pill would ever appear for it. `startSideChat`
    // no-ops when one already exists, which is what the row's own side chats
    // are passed in for.
    onStartSideChat: canStartSideChat
      ? async (item, existing: AssetChatSideChat[]) => {
          const originMessageId = item.originMessage?.id;
          if (!originMessageId) return;
          const author = item.originMessage?.createdBy;
          const authorName = [author?.name?.first, author?.name?.last]
            .filter(Boolean)
            .join(' ');
          // The menu invokes this as a void callback, so a rejection here
          // would be an unhandled one with nothing shown to the author.
          try {
            await startSideChat({
              issue: {
                id: item.id,
                workspace: { id: item.workspace?.id ?? subject.workspaceId },
              },
              originMessageId,
              authorName,
              summaries: new Map(existing.map((s) => [s.parentMessageId, s])),
            });
          } catch (error) {
            Toast.show({
              type: 'error',
              text1: t('chat.row.sideChatFailed'),
            });
            return;
          }
          navigate(`${assetBasePath}/chat/sidechat/${originMessageId}`);
        }
      : undefined,
    onCall: call
      ? (item) => {
          const author = item.originMessage?.createdBy ?? item.createdBy;
          if (!author?.id) return;
          const name = [author.name?.first, author.name?.last]
            .filter(Boolean)
            .join(' ');
          call.startCall({ id: author.id, name }, item.id);
        }
      : undefined,
    onCopy: async (item) => {
      const raw = item.originMessage?.text;
      if (!raw) return;
      // Same normalisation the thread's own Copy applies: mentions are stored
      // as `@[name](id)`, and the clipboard should carry what the reader sees.
      const text = replaceMentionPlaceholderWithNames(
        raw,
        (item.userMentions ?? []) as never,
        t('mentions.unknownUser'),
        true
      );
      try {
        await navigator.clipboard.writeText(text);
        Toast.show({ type: 'success', text1: t('chat.thread.copied') });
      } catch (error) {
        Toast.show({ type: 'error', text1: t('chat.thread.copyError') });
      }
    },
    onShare: (item) => setShareIssueId(item.id),
  };

  const rightColumn: ColumnData = {
    // The reference shows the asset name as the column title, not a generic
    // "Chat" label — the reader is already inside this asset, so "Chat" adds
    // nothing. 'assets.menu.titleSingular' below is only the fallback for
    // the rare case the name hasn't resolved.
    title: 'assets.menu.titleSingular',
    rawTitle: subject.workspaceAsset.asset?.name,
    headerComponent: (
      <AssetChatToolbar
        onCallDealer={canCallDealer ? onCallDealer : undefined}
        onToggleFilters={() =>
          setOpenBand((value) => value === 'filters' ? undefined : 'filters')
        }
        filtersOpen={openBand === 'filters'}
        streamFilter={streamFilter}
      >
        {canShareAsset && (
          <AssetChatHeaderMenu
            onShare={() =>
              setOpenBand((value) => value === 'share' ? undefined : 'share')
            }
            active={isBandOpen}
            testID="asset-chat-panel-band-toggle"
          />
        )}
      </AssetChatToolbar>
    ),
    mainComponent: (
      <>
        <AssetChatBand
          visible={isBandOpen}
          onClose={() => setOpenBand(undefined)}
          testID="asset-chat-panel-band"
        >
          <ShareAssetPanel
            workspaceAssetId={subject.workspaceAssetId}
            workspaceId={workspaceId}
            enabled={isBandOpen}
            testID="asset-chat-panel-share"
          />
          <ParticipantsDisclosure
            workspaceAssetId={subject.workspaceAssetId}
            workspaceName={chatWorkspace?.name}
            enabled={isBandOpen}
            testID="asset-chat-panel-participants"
          />
        </AssetChatBand>
        <AssetChatBand
          visible={openBand === 'filters'}
          variant="filters"
          testID="asset-chat-panel-filter-band"
        >
          <AssetChatFilters selected={streamFilter} onSelect={setStreamFilter} />
        </AssetChatBand>
        <AssetChatFeed
          streamFilter={streamFilter}
          subject={subject}
          filterKey={ActivityFilterKey.AssetChat}
          onOpenThread={(item: AssetChatFeedItem) =>
            navigate(`${assetBasePath}/chat/thread/${item.id}`)
          }
          mediaPicker={MediaPicker}
          rowActions={rowActions}
          testID="asset-chat-panel-feed"
        />
      </>
    ),
    showBackButton: true,
    onBackButtonPress: onBackToProfile,
    disableScroll: true,
  };

  // Thread chrome reuses the existing Issue detail view (the activity's
  // existing message thread, no new data path); `isAssetChatThread` swaps in
  // the compact single-row header, disables Side Chat entirely, and sets a
  // thread-specific composer placeholder in place of the classic
  // IssueHeaderCropCircle chrome.
  //
  // `isComingFromActivities` is deliberately left false (its default) even
  // under the activities prefix: true would route Issue's own asset-link tap
  // and back-press guard through hardcoded `/activities/:id/...` paths, which
  // are wrong for this panel's three mount points. Left false, Issue's back
  // press would otherwise fall through to `computeBackRoute`'s generic
  // segment-count rule, which for `.../chat/thread/:id` lands one level too
  // far back (the plain asset page, not `/chat`) — `backOverrideUrl` fixes
  // that by naming the one correct destination directly, sidestepping the
  // generic rule entirely for this caller.
  //
  // Known gap: Issue's own Side Chat trigger still hard-navigates to
  // /activities/:issueId/sidechat/:messageId, which would leave this panel.
  // Unreachable today — `isAssetChatThread` keeps `onOpenSideChat` unpassed,
  // so the trigger can't fire — but it becomes relevant again once the row
  // overflow menu gives Side Chat an entry point from the feed.
  const threadColumn: ColumnData = {
    title: 'activities.detail.title',
    showHeader: false,
    disableScroll: true,
    mainComponent: (
      <AssetRouteProvider value={assetRouteValue}>
        <Issue
          issueId={threadIssueId ?? ''}
          showAssetLink={false}
          backOverrideUrl={`${assetBasePath}/chat`}
          isAssetChatThread
          onOpenRoutine={onOpenRoutine}
        />
      </AssetRouteProvider>
    ),
  };

  /**
   * The side chat a feed row opened. Its source row is normally the anchor
   * message rendered as an ordinary message, but an anchor can exist and carry
   * no text and no media — the majority shape on a routine-heavy asset — and
   * `IssueMessage` renders nothing at all for that. Where that happens the
   * feed's own compact line stands in: same initials chip, author, muted
   * activity title and timestamp the reader just pressed in the feed, with no
   * thread handler because it is already at its destination.
   */
  const sidechatHasContent =
    !!sidechatOriginMessage?.text || (sidechatOriginMessage?.media?.length ?? 0) > 0;

  const sidechatColumn: ColumnData | undefined =
    sidechatIssue && sidechatOriginMessage
      ? {
          title: 'activity.sideChat.title',
          showHeader: false,
          disableScroll: true,
          mainComponent: (
            <SideChatPanel
              issue={sidechatIssue}
              originMessage={sidechatOriginMessage}
              onBack={() => navigate(`${assetBasePath}/chat`)}
              mediaPicker={MediaPicker}
              mediaUrl={getMediaBaseUrl()}
              originRow={
                sidechatHasContent ? undefined : (
                  <AssetChatRow
                    item={{
                      id: sidechatIssue.id,
                      title: sidechatIssue.title,
                      severity: sidechatIssue.severity,
                      type: sidechatIssue.type,
                      // The domain Issue types its audit fields as Date;
                      // the feed row takes the ISO strings GraphQL returns.
                      createdAt: String(sidechatIssue.createdAt),
                      effectiveCreationDate:
                        sidechatIssue.effectiveCreationDate,
                      updatedAt: String(sidechatIssue.updatedAt),
                      createdBy: sidechatOriginMessage.updatedBy
                        ? {
                            id: sidechatOriginMessage.updatedBy.id as string,
                            name: sidechatOriginMessage.updatedBy.name,
                          }
                        : undefined,
                    }}
                  />
                )
              }
            />
          ),
        }
      : undefined;

  // Held until both resolve rather than mounting a half-built column: the
  // route carries only a messageId, so the activity arrives one request later.
  const thirdColumn: ColumnData | undefined = threadIssueId
    ? threadColumn
    : sidechatMessageId
      ? sidechatColumn
      : undefined;

  return (
    <>
      <SlideLayout
        leftColumn={leftColumn}
        rightColumn={rightColumn}
        thirdColumn={thirdColumn}
        fourthColumn={(threadIssueId && routineColumn) || undefined}
        viewMode={ViewMode.DUAL}
        initialTransition={initialTransition}
      />
      {/* This is the per-activity share (one Issue, mints a Guest), still a
          row action and unrelated to asset-level sharing, which mounts
          above Participants in the header-kebab band instead (task 3.7).
          The panel mounts its own Portal, so it sits outside the layout. */}
      {shareIssueId && (
        <ShareIssuePanel
          issueId={shareIssueId}
          visible
          onDismiss={() => setShareIssueId(undefined)}
        />
      )}
    </>
  );
};

const styles = StyleSheet.create({
  headerActionsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
});
